import { c as _c } from "react/compiler-runtime";
import { QueryOrdering } from '@/api/generated/model';
import { TotalIntentAggregates } from '@/domains/home/components/home-tabs/user-intelligence/_components';
import { computeChartSortByTotalPercentage, getChartSortByTotalValue, getChartSortByIcon, isPercentageSortByValue } from '@/utils/chartview/metricsUtils';
import { cn } from '@/utils/shad';
import { PieChart } from 'lucide-react';
import { InteractionAggregatesPaginated } from '@/models/interaction-aggregates/internal-models';
import { IntelligenceTableRow } from '@/models/intelligence-table-row';
import React, { PropsWithChildren } from 'react';
const paddingXValue = 8;
type Props = {
  items: IntelligenceTableRow[];
  onClearButtonClick?: () => void;
  disableClearButton?: boolean;
  title: string;
  totals: TotalIntentAggregates | undefined;
  sortByCriteria: QueryOrdering['by'];
  aggregatesData: InteractionAggregatesPaginated | undefined;
};
export const ChartItemsListHeader = t0 => {
  const $ = _c(36);
  const {
    items,
    onClearButtonClick,
    disableClearButton: t1,
    title,
    sortByCriteria,
    totals,
    aggregatesData
  } = t0;
  const disableClearButton = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex items-center justify-between gap-1.5 py-2 text-[#777]", `px-${paddingXValue}`);
    t3 = cn("rounded-full border px-2");
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== items.length) {
    t4 = <span> ({items.length})</span>;
    $[2] = items.length;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4 || $[5] !== title) {
    t5 = <h2 className={t3}>{title}{t4}</h2>;
    $[4] = t4;
    $[5] = title;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = sortByCriteria === "retention" ? "hidden" : "";
  let t7;
  if ($[7] !== sortByCriteria) {
    t7 = getChartSortByIcon(sortByCriteria);
    $[7] = sortByCriteria;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== aggregatesData || $[10] !== items || $[11] !== sortByCriteria) {
    t8 = getChartSortByTotalValue(sortByCriteria, items, aggregatesData);
    $[9] = aggregatesData;
    $[10] = items;
    $[11] = sortByCriteria;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <span className="ml-2">{t8}</span>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <div className="flex items-center">{t7}{t9}</div>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== aggregatesData || $[19] !== sortByCriteria || $[20] !== totals) {
    t11 = isPercentageSortByValue(sortByCriteria) && totals && <div className="flex items-center"><PieChart size={12} className="-mr-1.5" /><span className="ml-2">{computeChartSortByTotalPercentage(sortByCriteria, totals, aggregatesData)}</span></div>;
    $[18] = aggregatesData;
    $[19] = sortByCriteria;
    $[20] = totals;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="flex items-center gap-2">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t6) {
    t13 = <div className={t6}>{t12}</div>;
    $[25] = t12;
    $[26] = t6;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <hr className="grow border-[#222]" />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== disableClearButton || $[30] !== onClearButtonClick) {
    t15 = !disableClearButton && <button className={cn("underline underline-offset-4")} onClick={() => onClearButtonClick?.()}>Clear x</button>;
    $[29] = disableClearButton;
    $[30] = onClearButtonClick;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15 || $[34] !== t5) {
    t16 = <header className={t2}>{t5}{t13}{t14}{t15}</header>;
    $[32] = t13;
    $[33] = t15;
    $[34] = t5;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
};
type HeadingWithSeparatorProps = PropsWithChildren & {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  headerClassName?: string;
  appendChildren?: React.ReactNode;
};
export function HeadingWithSeparator(t0) {
  const $ = _c(10);
  const {
    children,
    headerClassName,
    headingLevel: t1,
    appendChildren
  } = t0;
  const headingLevel = t1 === undefined ? "h2" : t1;
  const HComponent = headingLevel;
  let t2;
  if ($[0] !== headerClassName) {
    t2 = cn("rounded-full border border-neutral-700 px-2 text-neutral-500", headerClassName);
    $[0] = headerClassName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== HComponent || $[3] !== children || $[4] !== t2) {
    t3 = <HComponent className={t2}>{children}</HComponent>;
    $[2] = HComponent;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <hr className="grow" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== appendChildren || $[8] !== t3) {
    t5 = <div className="flex items-center justify-between gap-1.5 py-2">{t3}{t4}{appendChildren}</div>;
    $[7] = appendChildren;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}